/**
 * Connection manager with state machine
 */

import { ConnectionState } from './types';
import { EventEmitter } from './EventEmitter';

interface ConnectionManagerEvents {
  stateChange: { oldState: ConnectionState; newState: ConnectionState };
}

/**
 * Manages connection state transitions
 */
export class ConnectionManager extends EventEmitter<ConnectionManagerEvents> {
  private state: ConnectionState = ConnectionState.DISCONNECTED;

  /**
   * Get the current connection state
   */
  getState(): ConnectionState {
    return this.state;
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.state === ConnectionState.CONNECTED;
  }

  /**
   * Check if currently disconnected
   */
  isDisconnected(): boolean {
    return this.state === ConnectionState.DISCONNECTED;
  }

  /**
   * Check if currently connecting
   */
  isConnecting(): boolean {
    return this.state === ConnectionState.CONNECTING;
  }

  /**
   * Check if currently reconnecting
   */
  isReconnecting(): boolean {
    return this.state === ConnectionState.RECONNECTING;
  }

  /**
   * Transition to a new state
   * @param newState - The new state to transition to
   * @throws Error if the state transition is invalid
   */
  setState(newState: ConnectionState): void {
    const oldState = this.state;

    // Validate state transition
    if (!this.isValidTransition(oldState, newState)) {
      throw new Error(`Invalid state transition from ${oldState} to ${newState}`);
    }

    this.state = newState;
    this.emit('stateChange', { oldState, newState });
  }

  /**
   * Check if a state transition is valid
   * @param from - Current state
   * @param to - Target state
   * @returns Whether the transition is valid
   */
  private isValidTransition(from: ConnectionState, to: ConnectionState): boolean {
    // Allow staying in the same state
    if (from === to) {
      return true;
    }

    // Define valid transitions
    const validTransitions: Record<ConnectionState, ConnectionState[]> = {
      [ConnectionState.DISCONNECTED]: [
        ConnectionState.CONNECTING,
        ConnectionState.RECONNECTING, // Allow reconnection from disconnected state
      ],
      [ConnectionState.CONNECTING]: [
        ConnectionState.CONNECTED,
        ConnectionState.DISCONNECTED,
      ],
      [ConnectionState.CONNECTED]: [
        ConnectionState.DISCONNECTING,
        ConnectionState.RECONNECTING,
        ConnectionState.DISCONNECTED,
      ],
      [ConnectionState.RECONNECTING]: [
        ConnectionState.CONNECTING,
        ConnectionState.CONNECTED,
        ConnectionState.DISCONNECTED,
      ],
      [ConnectionState.DISCONNECTING]: [
        ConnectionState.DISCONNECTED,
      ],
    };

    return validTransitions[from]?.includes(to) ?? false;
  }

  /**
   * Reset to disconnected state
   */
  reset(): void {
    if (this.state !== ConnectionState.DISCONNECTED) {
      const oldState = this.state;
      this.state = ConnectionState.DISCONNECTED;
      this.emit('stateChange', { oldState, newState: ConnectionState.DISCONNECTED });
    }
  }

  /**
   * Transition to connecting state
   */
  connecting(): void {
    this.setState(ConnectionState.CONNECTING);
  }

  /**
   * Transition to connected state
   */
  connected(): void {
    this.setState(ConnectionState.CONNECTED);
  }

  /**
   * Transition to disconnecting state
   */
  disconnecting(): void {
    this.setState(ConnectionState.DISCONNECTING);
  }

  /**
   * Transition to disconnected state
   */
  disconnected(): void {
    this.setState(ConnectionState.DISCONNECTED);
  }

  /**
   * Transition to reconnecting state
   */
  reconnecting(): void {
    this.setState(ConnectionState.RECONNECTING);
  }
}

